<template>
	<div class="media-uploader">
		<input type="file" ref="filePicker" multiple accept="image/jpeg,image/png,image/webp" @change="onFilesPicked" :disabled="busy" hidden />

		<div class="dropzone" :class="{ disabled: isMaxReached || busy }" @click="triggerPicker">
			<button type="button" class="upload-btn" :disabled="isMaxReached || busy" @click.stop="triggerPicker">
				<span class="plus">+</span>
				<span>{{ busy ? 'Uploading...' : 'Upload' }}</span>
			</button>
			<p class="hint">{{ category }} · max {{ maxCount }} files · up to {{ humanSize(maxFileSize) }} each</p>
		</div>

		<div v-if="items.length" class="thumbs">
			<div v-for="(item, idx) in items" :key="item.orgUrl" class="thumb" @click="openLightbox(item)">
				<img :src="item.orgUrl" :alt="item.orgName || ''" />
				<button type="button" class="thumb-remove" @click.stop="remove(idx)" aria-label="Remove">&times;</button>
				<button v-if="sortable" type="button" class="thumb-move move-prev" :disabled="idx === 0" @click.stop="move(idx, -1)" aria-label="Move earlier" title="Move earlier">&lsaquo;</button>
				<button v-if="sortable" type="button" class="thumb-move move-next" :disabled="idx === items.length - 1" @click.stop="move(idx, 1)" aria-label="Move later" title="Move later">&rsaquo;</button>
			</div>
		</div>

		<ImageLightbox :visible="lightbox.visible" :src="lightbox.src" :alt="lightbox.alt" @close="closeLightbox" />
	</div>
</template>

<script>
import { apiUploadMedia } from '@/api/basicinfo/retail_setting';
import ImageLightbox from '@/components/common/ImageLightbox.vue';

export default {
	name: 'MediaUploader',
	components: { ImageLightbox },
	props: {
		value: { type: Object, default: () => ({ items: [] }) },
		category: { type: String, required: true },
		maxCount: { type: Number, required: true },
		maxFileSize: { type: Number, default: 5 * 1024 * 1024 },
	},
	data() {
		return {
			busy: false,
			lightbox: { visible: false, src: '', alt: '' },
		};
	},
	computed: {
		items() {
			return (this.value && this.value.items) || [];
		},
		sortable() {
			return this.maxCount > 1;
		},
		isMaxReached() {
			return this.items.length >= this.maxCount;
		},
		allowedMimes() {
			return ['image/jpeg', 'image/png', 'image/webp'];
		},
	},
	methods: {
		humanSize(bytes) {
			if (bytes >= 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + 'MB';
			if (bytes >= 1024) return (bytes / 1024).toFixed(1) + 'KB';
			return bytes + 'B';
		},
		triggerPicker() {
			if (this.isMaxReached || this.busy) return;
			this.$refs.filePicker.click();
		},
		async onFilesPicked(e) {
			const picked = Array.from(e.target.files || []);
			this.$refs.filePicker.value = '';
			if (!picked.length) return;

			if (this.items.length + picked.length > this.maxCount) {
				this.$emit('error', `Cannot exceed ${this.maxCount} file(s) for ${this.category}.`);
				return;
			}
			for (const f of picked) {
				if (!this.allowedMimes.includes(f.type)) {
					this.$emit('error', `Unsupported type: ${f.type || '(unknown)'}`);
					return;
				}
				if (f.size > this.maxFileSize) {
					this.$emit('error', `File too large: ${f.name} (${this.humanSize(f.size)})`);
					return;
				}
			}

			this.busy = true;
			try {
				const res = await apiUploadMedia(this.category, picked);
				const uploaded = res.data || [];
				const merged = {
					items: this.items.concat(
						uploaded.map(u => ({
							url: u.url,
							orgUrl: u.orgUrl,
							orgName: u.orgName,
							mimeType: u.mimeType,
							fileSizeBytes: u.fileSizeBytes,
						})),
					),
				};
				this.$emit('input', merged);
			} catch (err) {
				const msg = (err.response && err.response.data && err.response.data.message) || err.message || 'Upload failed';
				this.$emit('error', msg);
			} finally {
				this.busy = false;
			}
		},
		remove(idx) {
			const next = { items: this.items.slice() };
			next.items.splice(idx, 1);
			this.$emit('input', next);
		},
		move(idx, delta) {
			const target = idx + delta;
			if (target < 0 || target >= this.items.length) return;
			const next = this.items.slice();
			[next[idx], next[target]] = [next[target], next[idx]];
			this.$emit('input', { items: next });
		},
		openLightbox(item) {
			this.lightbox = { visible: true, src: item.orgUrl, alt: item.orgName || '' };
		},
		closeLightbox() {
			this.lightbox.visible = false;
		},
	},
};
</script>

<style scoped>
.media-uploader {
	font-size: 13px;
}
.dropzone {
	border: 1px dashed #ccc;
	border-radius: 4px;
	padding: 20px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	background: #fafafa;
	cursor: pointer;
	transition: border-color 0.15s, background 0.15s;
}
.dropzone:hover {
	border-color: #1e88e5;
	background: #f4f9ff;
}
.dropzone.disabled {
	cursor: not-allowed;
	opacity: 0.6;
}
.dropzone.disabled:hover {
	border-color: #ccc;
	background: #fafafa;
}
.upload-btn {
	background: #1e88e5;
	color: #fff;
	border: none;
	border-radius: 4px;
	padding: 8px 18px;
	font-size: 14px;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 6px;
}
.upload-btn[disabled] {
	background: #aac;
	cursor: not-allowed;
}
.upload-btn .plus {
	font-size: 18px;
	line-height: 1;
}
.hint {
	color: #888;
	margin: 0;
	font-size: 12px;
}
.thumbs {
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
	margin-top: 12px;
}
.thumb {
	position: relative;
	width: 110px;
	height: 110px;
	border: 1px solid #eee;
	border-radius: 4px;
	background: #fff;
	cursor: zoom-in;
	overflow: hidden;
}
.thumb img {
	width: 100%;
	height: 100%;
	object-fit: contain;
	display: block;
}
.thumb-remove {
	position: absolute;
	top: 4px;
	right: 4px;
	width: 22px;
	height: 22px;
	border-radius: 50%;
	border: none;
	background: rgba(0, 0, 0, 0.55);
	color: #fff;
	font-size: 16px;
	line-height: 1;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
}
.thumb-remove:hover {
	background: rgba(0, 0, 0, 0.75);
}
.thumb-move {
	position: absolute;
	bottom: 4px;
	width: 22px;
	height: 22px;
	border-radius: 50%;
	border: none;
	background: rgba(0, 0, 0, 0.55);
	color: #fff;
	font-size: 18px;
	line-height: 1;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
}
.thumb-move:hover:not([disabled]) {
	background: rgba(0, 0, 0, 0.75);
}
.thumb-move[disabled] {
	opacity: 0.35;
	cursor: not-allowed;
}
.move-prev {
	left: 4px;
}
.move-next {
	right: 4px;
}
</style>
